import React, { useContext, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FiChevronDown,
  FiHeart,
  FiMenu,
  FiSearch,
  FiShoppingBag,
  FiX,
} from "react-icons/fi";
import { CartContext } from "./CartContext";
import siteConfig from "../config/siteConfig";
import "../css/header.scss";

function Header() {
  const { cartCount, wishlistCount } = useContext(CartContext);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
        setSearchOpen(false);
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, []);

  const toTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
  const submitSearch = (event) => {
    event.preventDefault();
    const query = searchTerm.trim();
    navigate(query ? `/shop?search=${encodeURIComponent(query)}` : "/shop");
    setSearchOpen(false);
    toTop();
  };

  const shopLink = (item) => {
    const params = new URLSearchParams();
    if (item.category) params.set("category", item.category);
    if (item.categories) params.set("category", item.categories.join("|"));
    if (item.query) params.set(item.query, "true");
    const query = params.toString();
    return query ? `/shop?${query}` : "/shop";
  };

  return (
    <>
      <header className="site-header">
        <Link className="announcement-bar" to="/shop?new=true" onClick={toTop}>
          {siteConfig.announcement}
        </Link>

        <div className="header-shell">
          <button
            className="header-icon header-menu-trigger"
            type="button"
            aria-label="Open navigation"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(true)}
          >
            <FiMenu aria-hidden="true" />
          </button>

          <Link className="header-logo" to="/" onClick={toTop}>
            <img src={siteConfig.logo.src} alt={siteConfig.logo.alt} />
          </Link>

          <nav className="desktop-navigation" aria-label="Primary navigation">
            <Link className="nav-link" to="/shop" onClick={toTop}>
              Shop All
            </Link>
            <div className="nav-dropdown">
              <button type="button" className="nav-link">
                Catalogue <FiChevronDown aria-hidden="true" />
              </button>
              <div className="nav-dropdown-panel">
                <p className="nav-dropdown-title">Browse</p>
                {siteConfig.navigation.shop.map((item) => (
                  <Link key={item.label} to={shopLink(item)} onClick={toTop}>
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>

            <Link className="nav-link" to="/about" onClick={toTop}>
              Company
            </Link>

            <Link className="nav-link" to="/contact" onClick={toTop}>
              Support
            </Link>
          </nav>

          <div className="header-actions">
            <button
              className="header-icon header-search-button"
              type="button"
              aria-label="Search"
              aria-expanded={searchOpen}
              onClick={() => setSearchOpen((open) => !open)}
            >
              <FiSearch aria-hidden="true" />
              <span className="header-action-label">Search</span>
            </button>
            <Link
              className="header-icon header-wishlist"
              to="/shop?wishlist=true"
              aria-label={`Wishlist with ${wishlistCount} items`}
              onClick={toTop}
            >
              <FiHeart aria-hidden="true" />
              {wishlistCount > 0 && (
                <span className="header-count">{wishlistCount}</span>
              )}
            </Link>
            <Link
              className="header-icon"
              to="/cartpage"
              aria-label={`Shopping bag with ${cartCount} items`}
              onClick={toTop}
            >
              <FiShoppingBag aria-hidden="true" />
              <span className="header-action-label">Cart</span>
              {cartCount > 0 && (
                <span className="header-count">{cartCount}</span>
              )}
            </Link>
          </div>
        </div>

        {searchOpen && (
          <div className="header-search-panel" role="search">
            <form onSubmit={submitSearch}>
              <FiSearch aria-hidden="true" />
              <label className="sr-only" htmlFor="site-search">
                Search the collection
              </label>
              <input
                id="site-search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search for a style, material or category"
                autoFocus
              />
              <button type="submit">Search</button>
              <button
                type="button"
                className="search-close"
                onClick={() => setSearchOpen(false)}
                aria-label="Close search"
              >
                <FiX aria-hidden="true" />
              </button>
            </form>
          </div>
        )}
      </header>

      <div
        className={`mobile-menu-backdrop ${menuOpen ? "is-open" : ""}`}
        onClick={() => setMenuOpen(false)}
        aria-hidden="true"
      />
      <aside
        className={`mobile-menu ${menuOpen ? "is-open" : ""}`}
        aria-hidden={!menuOpen}
        aria-label="Mobile navigation"
      >
        <div className="mobile-menu-header">
          <img
            className="mobile-menu-wordmark"
            src={siteConfig.logo.src}
            alt={siteConfig.logo.alt}
          />
          <button
            type="button"
            className="header-icon"
            onClick={() => setMenuOpen(false)}
            aria-label="Close navigation"
          >
            <FiX aria-hidden="true" />
          </button>
        </div>
        <div className="mobile-menu-content">
          <p className="mobile-menu-eyebrow">Catalogue</p>
          {siteConfig.navigation.shop.map((item) => (
            <Link key={item.label} to={shopLink(item)} onClick={toTop}>
              {item.label}
            </Link>
          ))}
          <div className="mobile-menu-rule" />
          <Link to="/about" onClick={toTop}>
            Company
          </Link>
          <Link to="/contact" onClick={toTop}>
            Contact
          </Link>
          <Link to="/shop?wishlist=true" onClick={toTop}>
            Wishlist ({wishlistCount})
          </Link>
          <div className="mobile-menu-rule" />
          <p className="mobile-menu-eyebrow">Information</p>
          {siteConfig.moreLinks.map((item) => (
            <Link key={item.route} to={item.route} onClick={toTop}>
              {item.label}
            </Link>
          ))}
        </div>
      </aside>
    </>
  );
}

export default Header;
